import type { ResultsWidgetModel, ResultsWidgetModelEventBus } from '.';
import type { TableModel } from '../TableModel';
import { makeHeaderElements } from '../WidgetModel';
import type { WidgetHeaderElement } from '../WidgetModel';
import { COMMON_MENU_HEADER_ELEMENTS, STANDALONE_COMMON_MENU_HEADER_ELEMENTS } from '../WidgetModel/impl';
import type { IdeaSelectionResolvers } from './IdeaSelectionModel';
import {
    ResultsSelfOwnedModel,
    resultsWidgetInitialStateSchema,
    resultsWidgetStateSchema,
} from './ResultsSelfOwnedModel';
import type {
    ResultsSelfOwnedEventBus,
    ResultsWidgetInitialState,
    ResultsWidgetRuntimeState,
    ResultsWidgetState,
} from './ResultsSelfOwnedModel';
import { ResultsTableModel } from './ResultsTableModel';
import { computed, signal } from '@preact/signals-react';
import type { ReadonlySignal, Signal } from '@preact/signals-react';
import type { OutputColumnWithMetadata } from '@thinkalpha/platform-ws-client/contracts/table.js';
import type { Universe } from '@thinkalpha/platform-ws-client/contracts/universe.js';
import { shortid } from '@thinkalpha/ui-core/shortid';
import type { Logger } from '@thinkalpha/ui-ioc/core/types/Logger';
import type { ColumnTemplate } from '@thinkalpha/ui-types/contracts/column-template';
import { t } from 'i18next';
import { optional } from 'inversify';
import type { IdeaDTO, IdeaPlanDTO } from 'src/dtos/Idea';
import type { GridSettingsNavigationTarget } from 'src/features/grid-settings/models/NavigationModel';
import { inject, injectable } from 'src/features/ioc';
import { getValidationColumnDisplayName } from 'src/features/table/lib/validationColumns';
import type { ClientSortModel } from 'src/features/table/types';
import { UniversePlanDTO } from 'src/features/universe/dtos/UniversePlanDTO';
import { enableMultiIdeaSelect } from 'src/lib/featureFlags/flags/enableMultiIdeaSelect';
import { scrollingFastIdeaDwellTimeConfig } from 'src/lib/featureFlags/flags/scrollingFastIdeaDwellTimeConfig';
import { useWidgetTabIdForUserViewKey } from 'src/lib/featureFlags/flags/useWidgetTabIdForUserViewKey';
import { exportTableToCSV } from 'src/lib/util/exportTableCSV';
import { getIdeaDTOByIdQuery } from 'src/queries/ideas';
import { getUniversesQuery, getUniversesQueryData } from 'src/queries/universes';
import { getWorkspaceConfigQuery } from 'src/queries/workspace';
import type { ReactWidgetContainer } from 'src/ReactContainer';
import { generateWorkableColumnTemplateGridSettings } from 'src/routes/dashboard/widget-container/widget-settings-editor/context';
import type { WorkspaceService } from 'src/services/WorkspaceService';
import { decodeSelfOwnedWidgetState } from 'src/services/WorkspaceService/selfOwnedWidget';
import type { GetInitialData, WidgetId } from 'src/services/WorkspaceService/selfOwnedWidget';
import type { WidgetContainerController } from 'src/services/WorkspaceService/WidgetContainer/WidgetContainer';
import type { ReactBindings } from 'src/types/bindings';
import { EventBusBuilder, MergedEventBus } from 'src/types/EventBus/EventBus';
import { v4 } from 'uuid';

/** Header element types hidden on the Alpha Scans standalone page. */
export const ALPHA_SCANS_HIDDEN_HEADER_TYPES = new Set([
    'stickyFilter',
    'columnTemplateMenu',
    'settingsMenu',
    'ideasMenu',
]);

export interface IdeaResolverDeps {
    queryClient: ReactBindings['QueryClient'];
    formulaService: ReactBindings['FormulaService'];
    slugMapService: ReactBindings['SlugMapService'];
    logger: Logger;
}

/**
 * The Results widget's idea resolution strategies. `seedDefaultUniverse` is
 * the creation-time instruction (never persisted) to resolve the workspace
 * default universe when no universe was supplied; without it a null universe
 * id is trusted as an explicit "no universe". Module-level (not a method) so
 * the resolution behavior is unit testable without constructing the
 * DI-assembled model.
 */
export function buildResultsIdeaResolvers(
    seedDefaultUniverse: boolean,
    deps: IdeaResolverDeps,
): IdeaSelectionResolvers {
    const { queryClient, formulaService, slugMapService, logger } = deps;

    const fetchIdeaDTO = (id: string): Promise<IdeaDTO<IdeaPlanDTO>> =>
        queryClient.fetchQuery(getIdeaDTOByIdQuery(queryClient, formulaService, slugMapService, id)) as Promise<
            IdeaDTO<IdeaPlanDTO>
        >;

    return {
        resolveInitialUniverseId: async (persistedUniverseId) => {
            if (persistedUniverseId !== null) return persistedUniverseId;
            if (!seedDefaultUniverse) return null;
            // Brand-new widget with no supplied universe: seed the workspace
            // default so the scan is not unbounded. Resolving here — behind
            // the widget's loading state — keeps the workspace-config fetch
            // off the widget-creation path, so a freshly added widget paints
            // immediately.
            try {
                const config = await queryClient.fetchQuery(getWorkspaceConfigQuery());
                return config?.defaultUniverseId ?? null;
            } catch (error) {
                // Warn, not error: the scan still runs, just without the default bound.
                logger.warn('Results widget could not fetch the workspace config for its default universe', {
                    error,
                });
                return null;
            }
        },
        resolveFastIdeas: async (ideaIds) => {
            const results = await Promise.allSettled(ideaIds.map(fetchIdeaDTO));
            const resolved: IdeaDTO<IdeaPlanDTO>[] = [];
            results.forEach((result, index) => {
                if (result.status === 'fulfilled') {
                    resolved.push(result.value);
                } else {
                    // A dangling idea reference should not block the table.
                    logger.warn('Results widget dropped an unresolvable fast idea', {
                        ideaId: ideaIds[index],
                        error: result.reason,
                    });
                }
            });
            return resolved;
        },
        resolveSlowIdea: async (universeId) => {
            if (!universeId) return null;
            // Persisted slow-idea ids do not (yet) disambiguate universes from ideas; the
            // known-universe list is the classifier: in the list means universe, otherwise
            // idea. A failed fetch falls back to the last cached list before guessing.
            let universes: Universe[] | undefined;
            try {
                universes = await queryClient.fetchQuery(getUniversesQuery());
            } catch (error) {
                universes = getUniversesQueryData(queryClient);
                logger.error('Results widget could not fetch universes to classify its slow idea', {
                    universeId,
                    usingCachedList: universes !== undefined,
                    error,
                });
            }
            if (universes === undefined || universes.some((universe) => universe.id === universeId)) {
                // With no list at all, assume a universe: a wrong guess degrades to an
                // empty scan, while failing here would wedge the widget in loading.
                return UniversePlanDTO.fromUniverseId(universeId).toTempIdea();
            }
            return fetchIdeaDTO(universeId);
        },
    };
}

@injectable()
export class ResultsWidgetModelImpl implements ResultsWidgetModel {
    static readonly stateSchema = resultsWidgetStateSchema;
    static readonly initialStateSchema = resultsWidgetInitialStateSchema;

    static defaultState(): ResultsWidgetState {
        return {
            ideas: [],
            universeId: null,
            columnTemplate: null,
            scrolling: false,
            filter: null,
        };
    }

    static defaultInitialState(): ResultsWidgetInitialState {
        // A brand-new widget resolves the workspace default universe unless
        // its creator supplied one; saved payloads never carry the instruction.
        return { ...ResultsWidgetModelImpl.defaultState(), useDefaultUniverse: true, isAlphaScan: false };
    }

    static fromJSON(container: ReactWidgetContainer): ResultsWidgetModelImpl {
        const getInitialData = container.get('GetInitialData') as GetInitialData<unknown>;
        const raw = getInitialData();
        const validated = decodeSelfOwnedWidgetState(ResultsWidgetModelImpl.initialStateSchema, raw);
        container.bind('InitialWidgetState').toConstantValue(validated);
        return container.get('ResultsWidgetModel') as ResultsWidgetModelImpl;
    }

    readonly #model: ResultsSelfOwnedModel;
    readonly #tableModel: ResultsTableModel;
    readonly #table: TableModel;
    readonly #disposableStack: DisposableStack;
    readonly #log: Logger;

    readonly #isValidationModeEnabled: Signal<boolean> = signal(false);
    readonly #isGridSettingsDialogOpen: Signal<boolean> = signal(false);
    readonly #gridSettingsDialogTarget: Signal<GridSettingsNavigationTarget | null> = signal(null);

    readonly headerElements: WidgetHeaderElement[];
    readonly isAlphaScan: boolean;
    readonly eventBus: ResultsWidgetModelEventBus;

    constructor(
        @inject('WidgetId') @optional() tabId: WidgetId | undefined,
        @inject('WidgetContainerController') @optional() container: WidgetContainerController | undefined,
        @inject('InitialWidgetState') initialState: ResultsWidgetInitialState,
        @inject('Logger') logger: Logger,
        @inject('QueryClient') queryClient: ReactBindings['QueryClient'],
        @inject('FormulaService') formulaService: ReactBindings['FormulaService'],
        @inject('SlugMapService') slugMapService: ReactBindings['SlugMapService'],
        @inject('FeatureFlagService') featureFlagService: ReactBindings['FeatureFlagService'],
        @inject('TableModel') table: TableModel,
        @inject('WorkspaceService') @optional() workspaceService?: WorkspaceService,
    ) {
        // Construction acquires into a local stack: if any later step throws, `using` disposes
        // everything already acquired (nothing owns a half-constructed model). Success hands
        // ownership to the field via move().
        using disposableStack = new DisposableStack();
        const { useDefaultUniverse, isAlphaScan, ...persisted } = initialState;
        this.isAlphaScan = isAlphaScan;
        this.#log = logger.getSubLogger({ name: `ResultsWidget:${shortid()}` }, { component: 'table' });

        const hasWorkspaceService = workspaceService !== undefined;
        const hasTabId = tabId !== undefined;
        const hasContainer = container !== undefined;
        if (hasWorkspaceService && hasTabId !== hasContainer) {
            throw new Error('Results workspace model requires both WidgetId and WidgetContainerController');
        }
        const hasWorkspaceContext = hasWorkspaceService && hasTabId && hasContainer;

        this.#table = disposableStack.use(table);

        const ideaResolvers = buildResultsIdeaResolvers(useDefaultUniverse, {
            queryClient,
            formulaService,
            slugMapService,
            logger: this.#log,
        });
        const multiIdeaSelectEnabled = featureFlagService.getSignal(enableMultiIdeaSelect);

        if (hasWorkspaceContext) {
            if (tabId === undefined || container === undefined || workspaceService === undefined) {
                throw new Error(
                    'Results workspace model requires WidgetId, WidgetContainerController, and WorkspaceService',
                );
            }
            this.#model = disposableStack.use(
                new ResultsSelfOwnedModel({
                    mode: 'workspace',
                    isAlphaScan,
                    container,
                    tabId,
                    workspaceService,
                    persisted,
                    logger: this.#log,
                    queryClient,
                    ideaResolvers,
                    multiIdeaSelectEnabled,
                }),
            );
        } else {
            this.#model = disposableStack.use(
                new ResultsSelfOwnedModel({
                    mode: 'standalone',
                    isAlphaScan,
                    persisted,
                    logger: this.#log,
                    queryClient,
                    ideaResolvers,
                    multiIdeaSelectEnabled,
                }),
            );
        }

        const dwellConfig = featureFlagService.getSignal(scrollingFastIdeaDwellTimeConfig);
        const useTabIdForUserViewKey = featureFlagService.getSignal(useWidgetTabIdForUserViewKey);
        const uniqueMountId = v4();
        const userViewKey = computed(() =>
            useTabIdForUserViewKey.value && tabId !== undefined ? (tabId as string) : uniqueMountId,
        );

        this.#tableModel = disposableStack.use(
            new ResultsTableModel({
                table: this.#table,
                logger: this.#log,
                devtoolsName: 'ResultsWidget/table',
                inputState: this.#model.inputState,
                columnTemplate: this.#model.columnTemplate,
                fastIdeas: this.#model.fastIdeas,
                slowIdea: this.#model.slowIdea,
                fastIdeasCacheKey: this.#model.fastIdeasCacheKey,
                slowIdeaCacheKey: this.#model.slowIdeaCacheKey,
                clientSortModel: this.#model.clientSortModel,
                scrolling: this.#model.scrolling,
                stickyFilterValue: this.#model.stickyFilterValue,
                columnOptions: this.#model.columnOptions,
                validationModeEnabled: this.#isValidationModeEnabled,
                dwellTimeSeconds: computed(() => dwellConfig.value.scrollingDwellTimeSeconds),
                userViewKey,
            }),
        );

        // Header elements: full set in workspace tabs, trimmed on Alpha Scans.
        const baseElements = hasWorkspaceContext
            ? COMMON_MENU_HEADER_ELEMENTS
            : STANDALONE_COMMON_MENU_HEADER_ELEMENTS.filter((el) => el.type !== 'channelPicker');
        let headerElements = makeHeaderElements<ResultsWidgetModelEventBus>([
            ...baseElements,
            { type: 'scrollingTableIndicator', position: 16, alwaysVisible: true },
            { type: 'stickyFilter', position: 40, priority: 70 },
            {
                key: 'ideasMenu-slow',
                type: 'ideasMenu',
                position: 53,
                priority: 60,
                ideaType: 'slow',
                ideaPlanTypes: ['universe', 'screener', 'watchlist'],
                selectType: 'single',
                showSelected: true,
                allowDelete: false,
            },
            {
                key: 'ideasMenu-fast',
                type: 'ideasMenu',
                position: 54,
                priority: 60,
                ideaType: 'fast',
                ideaPlanTypes: ['if-then', 'scanner', 'formula'],
                selectType: 'multi',
                showSelected: true,
                allowDelete: false,
            },
            { type: 'columnTemplateMenu', position: 100, priority: 10, columnTemplateKey: 'ticker' },
            { type: 'settingsMenu', position: 102, priority: 50 },
            { type: 'tableRowCount', position: 110, priority: 30 },
            { type: 'connectionStatus', position: 111, priority: 40 },
            { type: 'exportCSVButton', position: 120, priority: 20 },
        ]);
        if (!hasWorkspaceContext) {
            headerElements = headerElements.filter((el) => !ALPHA_SCANS_HIDDEN_HEADER_TYPES.has(el.type));
        }
        this.headerElements = headerElements;

        this.eventBus = this.#buildEventBus();
        this.#disposableStack = disposableStack.move();
    }

    #buildEventBus(): ResultsWidgetModelEventBus {
        const connectionStatus = computed(() => ({
            status: this.#table.connectionStatus.value,
            progress: this.#table.progress.value,
            failureCount: this.#table.failureCount.value,
        }));

        const implBus = EventBusBuilder.create()
            .addSignal('widget::formattableGridSettings', () =>
                computed(() => {
                    const columnTemplate = this.#model.columnTemplate.value;
                    if (!columnTemplate) {
                        return { settings: null, defaults: null };
                    }
                    const sanitized = generateWorkableColumnTemplateGridSettings(columnTemplate);
                    return { settings: sanitized, defaults: sanitized };
                }),
            )
            .addSignal('widget::isGridSettingsDialogOpen', () => computed(() => this.#isGridSettingsDialogOpen.value))
            .addSignal('widget::gridSettingsDialogTarget', () => computed(() => this.#gridSettingsDialogTarget.value))
            .addEvent('widget::openGridSettingsDialog', (payload) => {
                this.#isGridSettingsDialogOpen.value = payload.isOpen;
                this.#gridSettingsDialogTarget.value =
                    payload.isOpen && (payload.targetTab !== undefined || payload.targetColumnId !== undefined)
                        ? { tab: payload.targetTab, columnId: payload.targetColumnId }
                        : null;
            })
            .addSignal('widget::connectionStatus', () => connectionStatus)
            .addSignal('widget::tableRowCount', () => this.#table.countSignal)
            .addSignal('widget::isScrollingTable', () => this.#table.isScrolling)
            .addSignal('widget::isValidationModeEnabled', () => computed(() => this.#isValidationModeEnabled.value))
            .addEvent('widget::toggleValidationMode', () => {
                this.#isValidationModeEnabled.value = !this.#isValidationModeEnabled.value;
            })
            .addSignal('widget::validationColumns', () => this.#tableModel.validationColumns)
            .addEvent('widget::exportCSV', () => {
                void this.#exportCSV();
            })
            .addEvent('widget::connectionStatusRefetch', () => {
                this.#table.refetch();
            })
            // Account/instrument stubs for CommonWidgetSignals compatibility.
            .addSignal('widget::selectedAccountIds', () => computed(() => [] as string[]))
            .addSignal('widget::selectedAccountId', () => computed(() => null))
            .addSignal('widget::isAccountSelectionValid', () => computed(() => true))
            .addSignal('widget::instrument', () => computed(() => ''))
            .build();

        return MergedEventBus.from(this.#model.eventBus as ResultsSelfOwnedEventBus).merge(
            implBus,
        ) as ResultsWidgetModelEventBus;
    }

    get state(): ReadonlySignal<ResultsWidgetRuntimeState> {
        return this.#tableModel.state;
    }

    get columnTemplate(): ReadonlySignal<ColumnTemplate | null> {
        return this.#model.columnTemplate;
    }

    get clientSortModel(): ReadonlySignal<ClientSortModel> {
        return this.#model.clientSortModel;
    }

    get validationColumns(): ReadonlySignal<OutputColumnWithMetadata[]> {
        return this.#tableModel.validationColumns;
    }

    get table(): TableModel {
        return this.#table;
    }

    get dirty(): ReadonlySignal<boolean> {
        return this.#model.dirty;
    }

    setClientSortModel(sortModel: ClientSortModel): void {
        this.#model.setClientSortModel(sortModel);
    }

    updateConcreteTemplate(template: ColumnTemplate): void {
        this.#model.updateConcreteTemplate(template);
    }

    userSelectedSymbol(symbol: string): void {
        this.#model.userSelectedSymbol(symbol);
    }

    async #exportCSV(): Promise<void> {
        const columnTemplate = this.#model.columnTemplate.value;
        if (columnTemplate == null) return;

        const columnNames = [
            ...columnTemplate.columns.map((col) => ({ id: col.id, name: col.name })),
            ...this.#tableModel.validationColumns.value.map((column) => ({
                id: column.id,
                name: getValidationColumnDisplayName(column),
            })),
        ].reduce(
            (names, { id, name }) => {
                names[id] = name;
                return names;
            },
            {} as Record<string, string>,
        );

        await exportTableToCSV(this.#table, columnNames, t);
    }

    toJSON(): ResultsWidgetState {
        return this.#model.toJSON();
    }

    markClean(): void {
        this.#model.markClean();
    }

    [Symbol.dispose](): void {
        this.#disposableStack.dispose();
    }
}
